import { getAgreementType } from '@/types';
import type { BenchmarkRate, TradeType } from '@/types';
import type { CashPool } from '@/types/cash-pool.types';
import type { CashPoolListParams } from '@/services/cash-pool/cash-pool-service';
import { todayUtcKey } from '@/utils/format';

/**
 * LR-631: does this trade pool its cash collateral?
 *
 * GMSLA books two shapes and only one of them pools - fee always carries a cash
 * pool, rebate carries none ("поле кэшпула вообще можно убирать", customer
 * 2026-08-24). GMRA books rebate-only (LR-617) and keeps its mandatory pool, so
 * the gate keys on the agreement family rather than on the mode alone.
 *
 * ONE exported predicate because the rule reaches five places - whether the Cash
 * Pool field renders, whether ITS OWN VALIDATOR demands a pool, whether the
 * registry is queried to price the leg, whether the blocking-validation summary
 * demands a pool, and whether the submit payload carries one. A copy that drifts
 * shows the trader a required field that is not on screen, with no way to clear
 * it - which is exactly what the validator did until LR-718, because it was the
 * one surface this list forgot to name.
 *
 * Collateral type is deliberately NOT folded in: a non-cash trade holds no pool
 * for a different reason (there is no cash to hold), and every caller that cares
 * already reads `collateral_type` beside this.
 */
export function poolsItsCash(
  tradeType: string | undefined,
  rateEntryMode: string | undefined,
): boolean {
  return (
    (tradeType ? getAgreementType(tradeType as TradeType) === 'gmra' : false)
    || rateEntryMode === 'fee'
  );
}

/** Backend `GMSLA_POOL_INTEREST_FROM` (billing/services/accrual.py): keep the two in step. */
export const GMSLA_POOL_INTEREST_FROM = '2026-11-09';

/**
 * Whether the pool accrues the interest on this booking's cash itself, so the
 * trade carries the fee alone. Backend `Position.pool_accrues_interest`, for a
 * trade the form has not booked yet; a booked one reads the flag off its payload.
 * UTC "today", like the backend.
 */
export function poolAccruesInterest(booking: {
  tradeType: string | undefined;
  rateEntryMode: string | undefined;
  collateralType: string | undefined;
  cashPool: number | null | undefined;
}): boolean {
  return (
    booking.rateEntryMode === 'fee'
    && booking.collateralType === 'cash'
    && booking.cashPool != null
    && (booking.tradeType === 'lend' || booking.tradeType === 'borrow')
    && todayUtcKey() >= GMSLA_POOL_INTEREST_FROM
  );
}

/**
 * The registry filter for the pools a trade of this counterparty and side may hold its cash in:
 * Trade Entry's dropdown, its rate preview and Assign cash pool. The backend re-checks the same
 * rules (`_cash_pool_rejection`); a currency rule change lands here and there.
 */
export function eligibleCashPoolParams(
  counterparty: number | undefined,
  tradeType: string | undefined,
): CashPoolListParams {
  return {
    counterparty,
    product: tradeType ? getAgreementType(tradeType as TradeType) : undefined,
    in_agreement_currency: true,
    is_active: true,
  };
}

/**
 * LR-631: is THIS booked trade priced by its cash pool?
 *
 * `poolsItsCash` answers the form's question - may a trade of this shape carry a
 * pool - and it is deliberately shape-only. A row that already exists has a
 * second, narrower answer, and only the backend can give it: `cost_follows_pool()`
 * carries two exclusions the form has no way to see. GMRA never follows its pool
 * (repo is priced by its own accrual branch), and neither does a pool the fixing
 * engine cannot price - so a legacy fee-booked repo, or a fee trade whose pool
 * sits on a benchmark with no feed, keeps its BOOKED benchmark on its OWN day
 * count, exactly like a fee trade with no pool at all.
 *
 * That answer ships on every position payload as `benchmark_follows_fixing`, so
 * read it rather than re-deriving the rule client-side (dp review, LR-631 Low 2):
 * a copy without the exclusions divides the cost leg by the POOL's basis where
 * the backend divides by the trade's, and 360 against 365 moves the Net Amount on
 * the amend confirmation by 1.4 % - the last figure a trader sees before PATCH.
 *
 * The mode is still checked here: the flag is also true for a floating REBATE
 * trade (LR-628, its own fixing), which a repo with a pool can be.
 *
 * `!== false` and not `=== true`: the field is optional on the payload types, and
 * an absent one means "an older serializer", not "not pooled".
 */
export function pricedByPool(trade: {
  cash_pool?: number | null;
  rate_entry_mode?: string;
  benchmark_follows_fixing?: boolean;
}): boolean {
  return (
    trade.cash_pool != null
    && trade.rate_entry_mode === 'fee'
    && trade.benchmark_follows_fixing !== false
  );
}

/**
 * LR-631: the rate a cash pool accrues at, and the basis it accrues on.
 *
 * A fee trade's collateral sits in a pool and earns the POOL's rate, so the
 * booking form prices its cost leg off this rather than off a benchmark the
 * trader types (customer, 2026-08-24: "не просто 3.63, а 3.63 плюс 5 bps").
 * The backend does the same for the daily accrual (`accrual._pool_cost_rate`)
 * and stamps the same figure onto the booking, so the preview and the invoice
 * describe one number.
 *
 * Rate and basis travel together: a pool is quoted as a contractual pair
 * (4.5 % on ACT/360), and pricing the rate on the trade's own day count would
 * preview a cost the statement never charges.
 *
 * `undefined` when there is no pool, or when a floating pool's benchmark has no
 * rate in the registry yet — the caller keeps whatever it was showing.
 */
export interface CashPoolRate {
  /** Percent, same convention as every other rate on the form (3.68 = 3.68 %). */
  rate: number;
  /** Day-count divisor: 360 or 365. */
  dayCount: number;
}

/**
 * Quantise to the 4 decimals `Position.benchmark_rate` is declared at.
 *
 * The backend builds the same figure in Decimal, where fixing + spread/100 is
 * EXACT at 4 dp; in float it is not. The customer's own example, 3.63 + 5 bps,
 * comes out 3.6799999999999997 — and that string is what the form posts as
 * `benchmark_rate`, whereupon the serializer answers "Ensure that there are no
 * more than 4 decimal places" about a field a pooled fee trade no longer even
 * shows. Quantising here rather than at the form field keeps the preview and the
 * payload on one number, and it is the number the backend would have computed.
 */
const to4dp = (value: number): number => Number(value.toFixed(4));

export function cashPoolRate(
  pool: CashPool | undefined,
  benchmarkRates: Partial<Record<BenchmarkRate, number>>
): CashPoolRate | undefined {
  if (!pool) return undefined;
  const dayCount = Number(pool.day_count_convention) || 360;

  if (pool.rate_type === 'fixed') {
    const fixed = pool.fixed_rate === null ? NaN : parseFloat(pool.fixed_rate);
    return Number.isFinite(fixed) ? { rate: to4dp(fixed), dayCount } : undefined;
  }

  if (!pool.benchmark_name || pool.spread_bps === null) return undefined;
  const benchmark = benchmarkRates[pool.benchmark_name];
  if (benchmark === undefined) return undefined;
  return { rate: to4dp(benchmark + pool.spread_bps / 100), dayCount };
}
